import React, { useLayoutEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import styled from "styled-components";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { FaLeaf } from "react-icons/fa";
import {
  FiHome,
  FiShoppingBag,
  FiUser,
  FiLogIn,
  FiGrid,
} from "react-icons/fi";

const BottomNavbar = ({
  isLoggedIn = false,
  onHomeClick,
  onFarmsClick,
  onBasketsClick,
  onLoginClick,
  onProfileClick,
  onFarmPanelClick,
  farmTaskCount = 0,
}) => {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const navRef = useRef(null);
  const [pressedId, setPressedId] = useState(null);
  const [pendingId, setPendingId] = useState(null);

  useLayoutEffect(() => {
    setPressedId(null);
    setPendingId(null);
  }, [pathname]);

  useLayoutEffect(() => {
    const node = navRef.current;
    if (!node) return undefined;
    const measure = () => {
      const height = node.getBoundingClientRect().height;
      const bottom = parseFloat(window.getComputedStyle(node).bottom) || 0;
      document.documentElement.style.setProperty("--mobile-nav-clearance", `${height ? Math.ceil(height + bottom + 8) : 0}px`);
    };
    measure();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(measure);
    observer?.observe(node);
    window.addEventListener("resize", measure);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", measure);
      document.documentElement.style.removeProperty("--mobile-nav-clearance");
    };
  }, []);

  const isActive = (id) => {
    switch (id) {
      case "home":
        return pathname === "/" || pathname === "/home";
      case "farms":
        return pathname.startsWith("/farms");
      case "baskets":
        return pathname.startsWith("/baskets");
      case "farm-panel":
        return pathname.startsWith("/villager");
      case "profile":
        return /^\/(profile|account|dashboard|orders|admin)(\/|$)/.test(pathname);
      case "login":
        return /^\/(login|signup|forgot-password|reset-password|confirm-email)(\/|$)/.test(pathname);
      default:
        return false;
    }
  };

  const tabs = [
    onHomeClick && {
      id: "home",
      label: t("nav.home", { defaultValue: "Home" }),
      icon: <FiHome />,
      onClick: onHomeClick,
    },
    onFarmsClick && {
      id: "farms",
      label: t("nav.farms", { defaultValue: "Farms" }),
      icon: <FaLeaf />,
      onClick: onFarmsClick,
    },
    // Baskets, not Cart: the floating cart button already covers the cart on mobile, so a
    // second entry point spent a nav slot on a destination that was one tap away anyway.
    // A farmer keeps this tab too — baskets are the whole marketplace, and the slot the
    // product catalogue used to hold is free now that products live under their farm.
    onBasketsClick && {
      id: "baskets",
      label: t("nav.baskets", { defaultValue: "Baskets" }),
      icon: <FiShoppingBag />,
      onClick: onBasketsClick,
    },
    onFarmPanelClick && {
      id: "farm-panel",
      label: t("nav.my_farm", { defaultValue: "My farm" }),
      icon: <FiGrid />,
      onClick: onFarmPanelClick,
      count: farmTaskCount,
    },
    isLoggedIn && onProfileClick
      ? {
          id: "profile",
          label: t("nav.profile", { defaultValue: "Profile" }),
          icon: <FiUser />,
          onClick: onProfileClick,
        }
      : onLoginClick
        ? {
            id: "login",
            label: t("nav.login", { defaultValue: "Log in" }),
            icon: <FiLogIn />,
            onClick: onLoginClick,
          }
        : null,
  ].filter(Boolean);

  const activeId = tabs.find(({ id }) => isActive(id))?.id;
  const bubbleId = pressedId ?? pendingId ?? activeId;
  const tabCount = tabs.length;

  useLayoutEffect(() => {
    const nav = navRef.current;
    if (!nav) return undefined;
    const positionBubble = () => {
      const button = Array.from(nav.querySelectorAll("button[data-tab]")).find(
        (item) => item.dataset.tab === bubbleId
      );
      if (!button) return;
      nav.style.setProperty("--bubble-x", `${button.offsetLeft}px`);
      nav.style.setProperty("--bubble-width", `${button.offsetWidth}px`);
    };
    positionBubble();
    const observer = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(positionBubble);
    observer?.observe(nav);
    window.addEventListener("resize", positionBubble);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", positionBubble);
    };
  }, [bubbleId, tabCount]);

  if (!tabs.length) return null;

  // Keep the dock outside page transitions and their stacking contexts.
  return createPortal(
    <NavbarContainer
      ref={navRef}
      role="navigation"
      aria-label={t("nav.mobile_nav", { defaultValue: "Mobile navigation" })}
    >
      <SelectionBubble aria-hidden="true" data-bubble-tab={bubbleId || ""} $visible={Boolean(bubbleId)} $pressed={Boolean(pressedId)} />
      {tabs.map(({ id, label, icon, onClick, count = 0 }) => {
        const active = isActive(id);
        return (
          <NavButton
            key={id}
            type="button"
            data-tab={id}
            onPointerDown={(event) => {
              if (event.button === 0) setPressedId(id);
            }}
            onPointerUp={() => setPressedId(null)}
            onPointerCancel={() => setPressedId((current) => current === id ? null : current)}
            onPointerLeave={() => setPressedId((current) => current === id ? null : current)}
            onKeyDown={(event) => {
              if (event.key === "Enter" || event.key === " ") setPressedId(id);
            }}
            onKeyUp={() => setPressedId(null)}
            onBlur={() => setPressedId((current) => current === id ? null : current)}
            onClick={() => {
              setPendingId(id);
              setPressedId(null);
              onClick();
            }}
            aria-label={
              count > 0
                ? `${label}, ${t("villager.home.standing", {
                    count,
                    defaultValue: "{{count}} things need you today.",
                  })}`
                : label
            }
            $active={active}
            $pressed={pressedId === id}
            {...(active ? { "aria-current": "page" } : {})}
          >
            <IconWrapper aria-hidden="true">
              {icon}
              {count > 0 ? (
                <TabCount aria-hidden="true">{count > 9 ? "9+" : count}</TabCount>
              ) : null}
            </IconWrapper>
            <NavLabel $active={active}>{label}</NavLabel>
          </NavButton>
        );
      })}
    </NavbarContainer>,
    document.body
  );
};

const NavbarContainer = styled.nav`
  position: fixed;
  left: max(${({ theme }) => theme.spacing(1.5)}, env(safe-area-inset-left, 0px));
  right: max(${({ theme }) => theme.spacing(1.5)}, env(safe-area-inset-right, 0px));
  bottom: calc(env(safe-area-inset-bottom, 0px) + ${({ theme }) => theme.spacing(1)});
  max-width: 600px;
  margin-inline: auto;
  border-radius: ${({ theme }) => theme.radius.pill}px;
  background: ${({ theme }) => theme.colors.background.surface};
  @supports ((backdrop-filter: blur(1px)) or (-webkit-backdrop-filter: blur(1px))) {
    background: color-mix(in srgb, ${({ theme }) => theme.colors.background.surface} 92%, transparent);
    -webkit-backdrop-filter: blur(18px);
    backdrop-filter: blur(18px);
  }
  border: 1px solid ${({ theme }) => theme.colors.border.subtle};
  box-shadow: ${({ theme }) => theme.colors.shadow.medium};
  display: flex;
  align-items: stretch;
  justify-content: space-around;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) =>
    `${theme.spacing(0.75)} ${theme.spacing(1)}`};
  @media (prefers-reduced-transparency: reduce), (prefers-contrast: more) {
    background: ${({ theme }) => theme.colors.background.surface};
    backdrop-filter: none;
    -webkit-backdrop-filter: none;
  }
  z-index: ${({ theme }) => theme.zIndex.cartBtn};

  @media (min-width: 769px) {
    display: none;
  }
`;

const SelectionBubble = styled.span`
  position: absolute;
  inset: ${({ theme }) => theme.spacing(0.75)} auto;
  left: 0;
  width: var(--bubble-width, 0px);
  transform: translate3d(var(--bubble-x, 0px), 0, 0)
    scale(${({ $pressed }) => $pressed ? "1.04, 1.06" : "1, 1"});
  border-radius: ${({ theme }) => theme.radius.pill}px;
  background: ${({ theme }) => theme.colors.state.selected};
  border: 1px solid ${({ theme }) => theme.colors.background.glassBorder};
  box-shadow: ${({ theme }) => theme.colors.shadow.low};
  opacity: ${({ $visible }) => $visible ? 1 : 0};
  pointer-events: none;
  transition: transform ${({ theme }) => theme.motion.base} cubic-bezier(0.22, 1, 0.36, 1),
    width ${({ theme }) => theme.motion.base} ease,
    opacity ${({ theme }) => theme.motion.fast} ease;
  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: translate3d(var(--bubble-x, 0px), 0, 0);
  }
`;

const NavButton = styled.button`
  position: relative;
  z-index: 1;
  border: none;
  background: transparent;
  flex: 1 1 0;
  min-width: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: ${({ theme }) => theme.spacing(0.5)};
  padding: ${({ theme }) => `${theme.spacing(0.5)} ${theme.spacing(0.5)}`};
  min-height: ${({ theme }) => theme.spacing(7)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  color: ${({ theme, $active }) =>
    $active ? theme.colors.text.link : theme.colors.text.secondary};
  font-size: ${({ theme }) => theme.typography.small};
  font-weight: 600;
  text-align: center;
  letter-spacing: 0.02em;
  cursor: pointer;
  touch-action: manipulation;
  user-select: none;
  -webkit-tap-highlight-color: transparent;
  transform: scale(${({ $pressed }) => $pressed ? 0.94 : 1});
  transition: background ${({ theme }) => theme.motion.fast} ease,
    color ${({ theme }) => theme.motion.fast} ease,
    transform ${({ theme }) => theme.motion.fast} ease;

  &:hover {
    color: ${({ theme }) => theme.colors.text.link};
  }

  &:focus-visible {
    outline: 3px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: -3px;
  }

  @media (prefers-reduced-motion: reduce) {
    transition: none;
    transform: none !important;
  }
`;

const IconWrapper = styled.span`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: ${({ theme }) => theme.spacing(2.5)};
  line-height: 1;
  width: ${({ theme }) => theme.spacing(5)};
  height: ${({ theme }) => theme.spacing(3.5)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};

  @media (prefers-reduced-motion: reduce) {
    transition: none;
  }
`;


const TabCount = styled.span`
  position: absolute;
  top: -${({ theme }) => theme.spacing(0.25)};
  right: ${({ theme }) => theme.spacing(0.5)};
  min-width: ${({ theme }) => theme.spacing(2)};
  height: ${({ theme }) => theme.spacing(2)};
  padding: 0 ${({ theme }) => theme.spacing(0.4)};
  border-radius: ${({ theme }) => `${theme.radius.pill}px`};
  background: ${({ theme }) => theme.colors.accent.danger};
  color: ${({ theme }) => theme.colors.text.inverse};
  font-size: 10px;
  font-weight: 700;
  line-height: ${({ theme }) => theme.spacing(2)};
  text-align: center;
`;

const NavLabel = styled.span`
  font-size: 11px;
  font-weight: ${({ $active }) => ($active ? 700 : 600)};
  line-height: 1.2;
  max-width: 100%;
  overflow-wrap: anywhere;
  color: ${({ theme }) => theme.colors.text.primary};
`;

export default BottomNavbar;
